import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function RefundCalculator() {
  useEffect(() => {
    document.title = "Tax Refund Calculator 2025 | Estimate Your IRS Refund - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Calculate your 2025 tax refund with our free IRS refund calculator. Estimate your federal tax refund or amount owed based on income, withholdings, deductions, and tax credits. Get accurate results in seconds.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'tax refund calculator, IRS refund estimator, federal tax refund, tax return calculator, refund estimate 2025');
    }
  }, []);

  const [income, setIncome] = useState('');
  const [withheld, setWithheld] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const [dependents, setDependents] = useState('0');
  const [credits, setCredits] = useState('0');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const standardDeductions: Record<string, number> = {
    single: 14600,
    married: 29200,
    head: 21900
  };

  const calculateRefund = () => {
    const grossIncome = parseFloat(income);
    const taxWithheld = parseFloat(withheld);
    const taxCredits = parseFloat(credits);
    const numDependents = parseInt(dependents);

    if (isNaN(grossIncome) || isNaN(taxWithheld)) {
      alert('Please enter valid amounts');
      return;
    }

    const deduction = standardDeductions[filingStatus];
    const taxableIncome = Math.max(0, grossIncome - deduction);
    
    // Simplified tax calculation
    let tax = 0;
    if (filingStatus === 'single') {
      if (taxableIncome <= 11600) tax = taxableIncome * 0.10;
      else if (taxableIncome <= 47150) tax = 1160 + (taxableIncome - 11600) * 0.12;
      else if (taxableIncome <= 100525) tax = 5426 + (taxableIncome - 47150) * 0.22;
      else if (taxableIncome <= 191950) tax = 17168.50 + (taxableIncome - 100525) * 0.24;
      else if (taxableIncome <= 243725) tax = 39110.50 + (taxableIncome - 191950) * 0.32;
      else if (taxableIncome <= 609350) tax = 55678.50 + (taxableIncome - 243725) * 0.35;
      else tax = 183647.50 + (taxableIncome - 609350) * 0.37;
    } else if (filingStatus === 'married') {
      if (taxableIncome <= 23200) tax = taxableIncome * 0.10;
      else if (taxableIncome <= 94300) tax = 2320 + (taxableIncome - 23200) * 0.12;
      else if (taxableIncome <= 201050) tax = 10852 + (taxableIncome - 94300) * 0.22;
      else if (taxableIncome <= 383900) tax = 34337 + (taxableIncome - 201050) * 0.24;
      else if (taxableIncome <= 487450) tax = 78221 + (taxableIncome - 383900) * 0.32;
      else if (taxableIncome <= 731200) tax = 111357 + (taxableIncome - 487450) * 0.35;
      else tax = 196669.50 + (taxableIncome - 731200) * 0.37;
    } else {
      if (taxableIncome <= 16550) tax = taxableIncome * 0.10;
      else if (taxableIncome <= 63100) tax = 1655 + (taxableIncome - 16550) * 0.12;
      else if (taxableIncome <= 100500) tax = 7241 + (taxableIncome - 63100) * 0.22;
      else if (taxableIncome <= 191950) tax = 15469 + (taxableIncome - 100500) * 0.24;
      else if (taxableIncome <= 243700) tax = 37417 + (taxableIncome - 191950) * 0.32;
      else if (taxableIncome <= 609350) tax = 53977 + (taxableIncome - 243700) * 0.35;
      else tax = 181954.50 + (taxableIncome - 609350) * 0.37;
    }

    // Child tax credit
    const childCredit = numDependents * 2000;
    const totalCredits = taxCredits + childCredit;

    const totalTax = Math.max(0, tax - totalCredits);
    const refundAmount = taxWithheld - totalTax;

    setResult({
      grossIncome,
      taxableIncome,
      totalTax,
      taxWithheld,
      totalCredits,
      refundAmount,
      isRefund: refundAmount > 0
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Refund Calculator</h1>
            <p className="text-lg text-gray-600">Estimate your tax refund or amount owed for 2026</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Income & Withholding</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Annual Gross Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="75000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Federal Tax Withheld</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={withheld}
                      onChange={(e) => setWithheld(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="12000"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">From your W-2 Box 2</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Filing Status</label>
                  <select
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Filing Jointly</option>
                    <option value="head">Head of Household</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Dependents</label>
                  <input
                    type="number"
                    value={dependents}
                    onChange={(e) => setDependents(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                    placeholder="0"
                    min="0"
                  />
                  <p className="text-sm text-gray-500 mt-1">$2,000 child tax credit per dependent</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Other Tax Credits</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={credits}
                      onChange={(e) => setCredits(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Education credits, EV credit, etc.</p>
                </div>

                <button
                  onClick={calculateRefund}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Calculate Refund
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Your Results</h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className={`rounded-xl p-6 ${result.isRefund ? 'bg-white/10' : 'bg-red-500/20'}`}>
                    <div className="text-sm text-teal-100 mb-1">
                      {result.isRefund ? 'Expected Refund' : 'Amount Owed'}
                    </div>
                    <div className="text-4xl font-bold flex items-center gap-2">
                      {result.isRefund ? (
                        <i className="ri-arrow-down-circle-line text-green-300"></i>
                      ) : (
                        <i className="ri-arrow-up-circle-line text-red-300"></i>
                      )}
                      ${Math.abs(result.refundAmount).toLocaleString(undefined, {maximumFractionDigits: 2})}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Gross Income</span>
                      <span className="font-semibold">${result.grossIncome.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Taxable Income</span>
                      <span className="font-semibold">${result.taxableIncome.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Total Tax</span>
                      <span className="font-semibold">${result.totalTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Tax Withheld</span>
                      <span className="font-semibold">${result.taxWithheld.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pb-3 border-b border-white/20">
                      <span className="text-teal-100">Total Credits</span>
                      <span className="font-semibold">${result.totalCredits.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6 mt-6">
                    <h3 className="font-semibold mb-3">What This Means</h3>
                    <p className="text-teal-100 text-sm leading-relaxed">
                      {result.isRefund
                        ? 'You overpaid your taxes and should receive a refund. The IRS typically processes refunds within 21 days of filing.'
                        : 'You underpaid your taxes and will owe this amount. Consider adjusting your W-4 withholding to avoid owing next year.'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-teal-100">
                    <i className="ri-money-dollar-circle-line text-6xl mb-4 opacity-50"></i>
                    <p>Enter your information to calculate your refund or amount owed</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}